import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  Timer, ChevronDown, ChevronUp, ArrowRight, Check, X, FileText,
  Scale, Wallet, ShieldCheck, Calculator, Landmark
} from "lucide-react";

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-200 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        style={{ display: "flex" }} className="w-full flex items-center justify-between py-4 text-left"
        data-testid={`faq-toggle-${question.slice(0, 20).replace(/\s/g, '-')}`}
      >
        <span className="font-medium text-gray-900 pr-4">{question}</span>
        {open ? <ChevronUp className="h-5 w-5 text-gray-400 flex-shrink-0" /> : <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />}
      </button>
      <p className={`text-gray-600 pb-4 text-sm leading-relaxed ${open ? "block" : "hidden"}`}>{answer}</p>
    </div>
  );
}

export default function ZeiterfassungOesterreich() {
  const navigate = useNavigate();

  useSEO({
    title: "Zeiterfassung für Freelancer in Österreich – EPU-Guide | ivoliq",
    description: "Zeiterfassung für EPU & Freelancer in Österreich: Rechtslage, Honorarnoten aus Stunden, SVS & Steuer im Blick. Timer bis Honorarnote mit einem Klick. Kostenlos starten.",
    canonical: "https://ivoliq.io/zeiterfassung-freelancer-oesterreich",
  });

  const atReasons = [
    {
      icon: Scale,
      title: "Nachweis bei Honorarstreit",
      desc: "Bei Werkverträgen und Honorarklagen zählt die Dokumentation. Eine lückenlose Stundenaufstellung ist vor Gericht dein stärkstes Beweismittel – ohne sie steht Aussage gegen Aussage.",
    },
    {
      icon: ShieldCheck,
      title: "Schutz vor Scheinselbständigkeit",
      desc: "ÖGK und Finanzamt prüfen, ob du wirklich selbständig arbeitest. Eigene Zeiterfassung mit freier Zeiteinteilung dokumentiert deine unternehmerische Unabhängigkeit vom Auftraggeber.",
    },
    {
      icon: Wallet,
      title: "SVS-Beiträge im Griff",
      desc: "26,83 % deines Gewinns gehen an die SVS. Nur wer weiß, wie viele Stunden er wirklich für ein Honorar arbeitet, erkennt unrentable Aufträge – bevor die Nachbemessung kommt.",
    },
    {
      icon: Landmark,
      title: "Basis für die Einkommensteuer",
      desc: "Deine Stundenaufzeichnungen stützen die Einnahmen-Ausgaben-Rechnung und machen Honorarnoten für das Finanzamt nachvollziehbar – besonders bei pauschalen Projektpreisen.",
    },
  ];

  const faqItems = [
    {
      q: "Bin ich als Freelancer in Österreich zur Zeiterfassung verpflichtet?",
      a: "Nein. Das Arbeitszeitgesetz (AZG) und die daraus folgende Arbeitszeitaufzeichnungspflicht gelten nur für Arbeitnehmer, nicht für EPU, Neue Selbständige oder Gewerbetreibende. ABER: Sobald du selbst Mitarbeiter beschäftigst, musst du deren Arbeitszeiten aufzeichnen (§ 26 AZG). Und für dich selbst ist die Zeiterfassung freiwillig, aber wirtschaftlich fast unverzichtbar.",
    },
    {
      q: "Was gilt für Neue Selbständige vs. Gewerbetreibende?",
      a: "Für die Zeiterfassung macht es keinen Unterschied: Beide sind nicht zur eigenen Arbeitszeitaufzeichnung verpflichtet. Beide zahlen SVS-Beiträge (26,83 % vom Gewinn) und profitieren gleichermaßen davon, den effektiven Stundensatz pro Auftrag zu kennen.",
    },
    {
      q: "Brauche ich eine Stundenaufstellung für meine Honorarnote?",
      a: "Rechtlich verpflichtend ist sie nicht, aber viele Auftraggeber verlangen einen Leistungsnachweis. Mit ivoliq hängst du die Stundenaufstellung automatisch an die Honorarnote an – jede Position mit Datum, Tätigkeit und Dauer. Das reduziert Rückfragen und beschleunigt die Zahlung.",
    },
    {
      q: "Hilft Zeiterfassung gegen den Vorwurf der Scheinselbständigkeit?",
      a: "Ja, sie ist ein Baustein. Wenn du deine Zeiten selbst und mit eigenem Tool erfasst (statt in das Zeitsystem des Auftraggebers eingebunden zu sein), dokumentiert das deine freie Zeiteinteilung – ein zentrales Kriterium bei der Abgrenzung zum echten Dienstverhältnis durch ÖGK und Finanzamt.",
    },
    {
      q: "Wie erstelle ich aus erfassten Stunden eine Honorarnote?",
      a: "Mit ivoliq wählst du die gewünschten Zeiteinträge aus und klickst auf 'Rechnung erstellen'. Die Honorarnote enthält automatisch alle Pflichtangaben nach § 11 UStG – inklusive UID-Nummer, fortlaufender Rechnungsnummer und korrektem USt-Satz (20 % bzw. Kleinunternehmer-Hinweis).",
    },
    {
      q: "Was muss ich als Kleinunternehmer in Österreich beachten?",
      a: "Bis 55.000 € Jahresumsatz (Grenze seit 2025) bist du von der Umsatzsteuer befreit. Deine Honorarnoten enthalten dann keinen USt-Ausweis, sondern den Hinweis auf die Kleinunternehmerregelung (§ 6 Abs 1 Z 27 UStG). ivoliq setzt das automatisch richtig um.",
    },
    {
      q: "Kann ich mit ivoliq auch meine Steuerrücklagen planen?",
      a: "Ja. ivoliq berechnet aus deinen Einnahmen automatisch, wie viel du für SVS und Einkommensteuer zurücklegen solltest – inklusive Investitionsfreibetrag (IFB). So kommt die SVS-Nachbemessung nie wieder überraschend.",
    },
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold text-gray-900" data-testid="header-logo">ivoliq</Link>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm text-gray-600 hover:text-gray-900">Anmelden</Link>
            <Button size="sm" onClick={() => navigate("/signup")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="header-cta">
              Kostenlos starten
            </Button>
          </div>
        </div>
      </header>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      {/* HERO */}
      <section className="pt-24 pb-16 bg-gradient-to-b from-emerald-50 to-white" data-testid="hero-section">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
            <Timer className="h-4 w-4" /> Für EPU & Freelancer in Österreich
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
            Zeiterfassung für Freelancer<br />
            <span className="text-emerald-600">in Österreich</span>
          </h1>
          <p className="text-lg text-gray-600 mt-6 max-w-2xl mx-auto leading-relaxed">
            Vom Timer zur fertigen Honorarnote nach § 11 UStG – mit SVS- und
            Steuer-Überblick. Gebaut für österreichische EPU. Kostenlos.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
            <Button size="lg" onClick={() => navigate("/signup")} className="bg-emerald-600 hover:bg-emerald-700 text-white px-8" data-testid="hero-cta-register">
              Kostenlos starten <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate("/stundensatz-rechner")} data-testid="hero-cta-calculator">
              <Calculator className="h-5 w-5 mr-2" /> Stundensatz berechnen
            </Button>
          </div>
          <p className="text-sm text-gray-500 mt-4">
            Keine Kreditkarte nötig &middot; Honorarnoten nach § 11 UStG &middot; SVS-Rücklagen-Rechner
          </p>
        </div>
      </section>

      {/* ==================== RECHTSLAGE AT ==================== */}
      <section className="py-16 bg-white" data-testid="legal-section">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 text-center mb-4">
            Muss ich als Selbständiger in Österreich Zeiten erfassen?
          </h2>
          <p className="text-gray-600 text-center mb-10 max-w-2xl mx-auto text-sm leading-relaxed">
            Die kurze Antwort: Nein, für dich selbst gibt es keine gesetzliche Pflicht.
            Die lange Antwort: Es gibt drei Situationen, in denen Zeiterfassung trotzdem entscheidend wird.
          </p>
          <div className="grid md:grid-cols-2 gap-6 mb-10">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-bold text-emerald-800 mb-3 flex items-center gap-2">
                <Check className="h-5 w-5" /> Keine Pflicht für dich als EPU
              </h3>
              <p className="text-sm text-emerald-700 leading-relaxed">
                Das Arbeitszeitgesetz (AZG) gilt nur für Arbeitnehmer. Als EPU, Neuer Selbständiger
                oder Gewerbetreibender entscheidest du selbst, ob und wie du deine Zeiten erfasst.
                Auch das EuGH-Urteil zur Arbeitszeiterfassung (C-55/18) betrifft nur Beschäftigte.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-bold text-amber-800 mb-3 flex items-center gap-2">
                <X className="h-5 w-5" /> Pflicht, sobald du Mitarbeiter hast
              </h3>
              <p className="text-sm text-amber-700 leading-relaxed">
                Beschäftigst du auch nur einen Dienstnehmer (auch geringfügig), musst du dessen
                Arbeitszeiten nach § 26 AZG aufzeichnen. Verstöße können Verwaltungsstrafen
                nach sich ziehen – pro Mitarbeiter und Delikt.
              </p>
            </div>
          </div>
          <h3 className="font-bold text-gray-900 text-center mb-6">Warum smarte EPU trotzdem jede Stunde erfassen</h3>
          <div className="grid sm:grid-cols-2 gap-6">
            {atReasons.map((r) => (
              <div key={r.title} className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <r.icon className="h-5 w-5 text-emerald-600" />
                  </div>
                  <h4 className="font-semibold text-gray-900">{r.title}</h4>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== HONORARNOTE WORKFLOW ==================== */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 text-center mb-4">
            Von der erfassten Stunde zur fertigen Honorarnote
          </h2>
          <p className="text-gray-600 text-center mb-12 max-w-xl mx-auto text-sm">
            ivoliq verbindet Zeiterfassung und Honorarnoten-Erstellung – speziell für Österreich.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "1", title: "Stunden erfassen", desc: "Timer starten oder Zeiten manuell nachtragen. Jeder Eintrag mit Projekt, Kunde und Tätigkeit – die spätere Leistungsbeschreibung schreibt sich von selbst." },
              { step: "2", title: "Zeiteinträge auswählen", desc: "Am Monatsende wählst du alle offenen Einträge eines Kunden aus. ivoliq gruppiert sie nach Tätigkeit und rechnet mit deinem hinterlegten Stundensatz." },
              { step: "3", title: "Honorarnote senden", desc: "Rechnung mit allen Pflichtangaben nach § 11 UStG: UID, fortlaufende Nummer, 20 % USt oder Kleinunternehmer-Hinweis. Stundenaufstellung als Anhang inklusive." },
            ].map((s) => (
              <div key={s.step} className="text-center">
                <div className="w-14 h-14 rounded-full bg-emerald-600 text-white flex items-center justify-center mx-auto text-xl font-bold mb-4">
                  {s.step}
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{s.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/zeiterfassung-freelancer#rechnung" className="text-emerald-600 underline font-medium text-sm" data-testid="link-bridge-page">
              Mehr dazu: Von der Zeiterfassung zur Rechnung – der komplette Workflow
            </Link>
          </div>
        </div>
      </section>

      {/* ==================== SVS & STEUER ==================== */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 text-center mb-8">
            Dein Stundensatz muss SVS & Einkommensteuer verdienen
          </h2>
          <div className="bg-gray-900 rounded-xl p-8 text-white">
            <p className="text-gray-300 text-sm leading-relaxed mb-6">
              Rechenbeispiel: Du verrechnest 80 € pro Stunde. Davon gehen ab –
            </p>
            <div className="space-y-3 text-sm max-w-md mx-auto">
              <div className="flex justify-between border-b border-gray-700 pb-2">
                <span className="text-gray-400">Honorar pro Stunde</span>
                <span className="font-semibold">80,00 €</span>
              </div>
              <div className="flex justify-between border-b border-gray-700 pb-2">
                <span className="text-gray-400">− SVS-Beiträge (26,83 %)</span>
                <span className="font-semibold text-red-400">− 21,46 €</span>
              </div>
              <div className="flex justify-between border-b border-gray-700 pb-2">
                <span className="text-gray-400">− Einkommensteuer (ca. 25 %)</span>
                <span className="font-semibold text-red-400">− 14,64 €</span>
              </div>
              <div className="flex justify-between pt-1">
                <span className="text-gray-300 font-medium">Bleibt dir netto (ca.)</span>
                <span className="font-bold text-emerald-400 text-lg">43,90 €</span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-6 text-center">
              Vereinfachte Darstellung ohne Betriebsausgaben, Gewinnfreibetrag & IFB. Keine Steuerberatung.
            </p>
          </div>
          <div className="text-center mt-8">
            <p className="text-gray-600 text-sm mb-4">
              Berechne, welcher Stundensatz für dich wirklich nötig ist – inklusive SVS und Steuer:
            </p>
            <Button onClick={() => navigate("/stundensatz-rechner")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="svs-calculator-cta">
              <Calculator className="h-4 w-4 mr-2" /> Zum Stundensatz-Rechner (AT)
            </Button>
          </div>
        </div>
      </section>

      {/* ==================== FAQ ==================== */}
      <section className="py-16 bg-gray-50" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 text-center mb-8">
            Häufige Fragen: Zeiterfassung in Österreich
          </h2>
          <div className="bg-white rounded-xl border shadow-sm p-6" data-testid="faq-list">
            {faqItems.map((item, i) => (
              <FAQItem key={i} question={item.q} answer={item.a} />
            ))}
          </div>
        </div>
      </section>

      {/* ==================== INTERNAL LINKS ==================== */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-gray-600 mb-4">Weiterführende Guides für Österreich & DACH:</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/zeiterfassung-freelancer" className="text-emerald-600 hover:text-emerald-700 underline text-sm">Zeiterfassung für Freelancer (Überblick)</Link>
            <Link to="/zeiterfassung-freelancer#rechnung" className="text-emerald-600 hover:text-emerald-700 underline text-sm">Zeiterfassung & Rechnungsstellung</Link>
            <Link to="/stundensatz-rechner" className="text-emerald-600 hover:text-emerald-700 underline text-sm">Stundensatz-Rechner</Link>
            <Link to="/buchhaltung-freelancer-oesterreich" className="text-emerald-600 hover:text-emerald-700 underline text-sm">Buchhaltung für Freelancer in Österreich</Link>
            <Link to="/steuerruecklagen-freelancer-oesterreich" className="text-emerald-600 hover:text-emerald-700 underline text-sm">Steuerrücklagen in Österreich berechnen</Link>
            <Link to="/rechnung-erstellen-kostenlos-oesterreich" className="text-emerald-600 hover:text-emerald-700 underline text-sm">Rechnung erstellen für Österreich</Link>
          </div>
        </div>
      </section>

      {/* ==================== FINAL CTA ==================== */}
      <section className="py-16 bg-emerald-600" data-testid="final-cta-section">
        <div className="max-w-3xl mx-auto px-4 text-center text-white">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">
            Jede Stunde zählt – auch in Österreich
          </h2>
          <p className="text-emerald-100 mb-8">
            Timer starten, Stunden erfassen, Honorarnote mit einem Klick. Kostenlos für EPU.
          </p>
          <Button size="lg" onClick={() => navigate("/signup")} className="bg-white text-emerald-700 hover:bg-emerald-50 px-8" data-testid="final-cta-register">
            Kostenlos starten <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
          <p className="text-sm text-emerald-200 mt-4">
            Keine Kreditkarte &middot; § 11 UStG-konform &middot; DSGVO-konform
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
